import axios from "axios";
import React, { useRef, useCallback, useMemo } from "react";
import { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import swal from "sweetalert";
import { BASE_URL } from "../Const/Url";
import DeleteIcon from "../CustomCommons/DeleteIcon";
import EditIcon from "../CustomCommons/EditIcon";
import Loader from "../CustomCommons/Loader";
import NextIcon from "../CustomCommons/NextIcon";
import Permission from "../CustomCommons/Permission";
import PreviousIcon from "../CustomCommons/PreviousIcon";
import kaarujLogo from "../../assets/Icon/KAARUJ 1.png";
import {
  can_add_invoice,
  can_change_invoice,
  can_delete_invoice,
  can_view_invoice,
  has_permissions,
} from "../CustomCommons/utils";
import ViewIcon from "../CustomCommons/ViewIcon";
import * as path from "../Routes/RoutePaths";
import { showToast } from "../../utils/ToastHelper";
import { logout_func2 } from "../Const/logoutCommon";
import { ArrowDown, ArrowUp } from "phosphor-react";
import HelperExportComponent from "../../utils/HelperExportComponent";

// Constants
const ITEMS_PER_PAGE = 20;
const SOURCE_LIST = ["Website", "Inventory", "Admin"];
const INVOICE_STATUS_CHOICES = ["Paid", "Unpaid", "Due", "Exchanged"];
const DELIVERY_STATUS_CHOICES = [
  "Returned",
  "Order Placed",
  "Delivered",
  "Pending",
  "Hold",
  "Dispatched",
];
const DELIVERY_TYPE_CHOICES = ["Regular", "Urgent"];
const PAYMENT_TYPE_CHOICES = [
  "C.O.D",
  "Card",
  "Bank Transfer",
  "bKash",
  "SSLECOMMERZ",
  "Nagad",
  "KOD",
];

const DELIVERY_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "&delivery_status=1", label: "Order Placed" },
  { value: "&delivery_status=0", label: "Returned" },
  { value: "&delivery_status=2", label: "Delivered" },
  { value: "&delivery_status=3", label: "Pending" },
  { value: "&delivery_status=4", label: "Hold" },
  { value: "&delivery_status=5", label: "Dispatched" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "&payment_status=0", label: "Paid" },
  { value: "&payment_status=1", label: "Unpaid" },
  { value: "&payment_status=2", label: "Due" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All" },
  { value: "1", label: "Inventory" },
  { value: "0", label: "Website" },
];

// Utility functions
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const buildApiUrl = (params) => {
  const {
    limit = ITEMS_PER_PAGE,
    offset = 0,
    query = "",
    invoiceSource = "",
    sortBy = "",
    date1 = "",
    date2 = "",
    deliveryStatus = "",
    paymentStatus = "",
  } = params;

  const baseUrl = `${BASE_URL}api/v1/sales/inventory/invoice/`;
  const queryParams = new URLSearchParams({
    limit,
    offset,
    query,
    is_custom: "false",
    is_outlet: "False",
    is_purchase_order: "False",
    is_requisition_order: "False",
  });

  if (invoiceSource !== "") queryParams.append("source", invoiceSource);
  if (sortBy) queryParams.append("ordering", sortBy);
  if (date1) queryParams.append("start", date1);
  if (date2) queryParams.append("end", date2);

  return `${baseUrl}?${queryParams.toString()}${deliveryStatus}${paymentStatus}`;
};

const InvoiceList = () => {
  const history = useHistory();
  const printRef = useRef();

  // Consolidated state
  const [state, setState] = useState({
    isLoading: true,
    isLoading2: true,
    totalData: 0,
    items: [],
    pageNumber: 1,
    nextUrl: null,
    prevUrl: null,
    checkedIds: [],
    sortBy: "",
    sortState: false,
    onLogoutHide: 0,
  });

  // Filter state
  const [filters, setFilters] = useState({
    query: "",
    invoiceSource: "",
    deliveryStatus: "",
    paymentStatus: "",
    date1: "",
    date2: "",
  });

  // Debounced query state for API calls
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Computed values
  const pageCount = Math.ceil(state.totalData / ITEMS_PER_PAGE);
  const offset = (state.pageNumber - 1) * ITEMS_PER_PAGE;
  const currentDate = formatDate(new Date());

  // Memoized values
  const filteredData = useMemo(() => {
    if (!filters.query) return state.items;

    const keys = ["number", "prepared_by", "delivery_status", "invoice_date"];
    return state.items.filter((item) =>
      keys.some((key) =>
        item[key]
          ?.toString()
          ?.toLowerCase()
          ?.includes(filters.query.toLowerCase())
      )
    );
  }, [state.items, filters.query]);

  // API function with error handling
  const fetchInvoices = useCallback(
    async (url) => {
      try {
        setState((prev) => ({ ...prev, isLoading: true }));

        const response = await axios.get(url);
        const { data } = response.data;

        setState((prev) => ({
          ...prev,
          totalData: data.count,
          items: data.results,
          nextUrl: data.next,
          prevUrl: data.previous,
          isLoading: false,
        }));
      } catch (error) {
        console.error("Error fetching invoices:", error);

        if (error.response?.status === 401) {
          logout_func2(state.onLogoutHide, history);
        }

        setState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [history, state.onLogoutHide]
  );

  // Effect for fetching data
  useEffect(() => {
    const url = buildApiUrl({
      limit: ITEMS_PER_PAGE,
      offset,
      query: debouncedQuery,
      invoiceSource: filters.invoiceSource,
      sortBy: state.sortBy,
      date1: filters.date1,
      date2: filters.date2,
      deliveryStatus: filters.deliveryStatus,
      paymentStatus: filters.paymentStatus,
    });

    fetchInvoices(url);
  }, [
    state.pageNumber,
    debouncedQuery,
    filters.invoiceSource,
    state.sortBy,
    filters.deliveryStatus,
    filters.paymentStatus,
    filters.date1,
    filters.date2,
    fetchInvoices,
  ]);

  // Pagination handlers
  const handlePaginationPrevious = useCallback(() => {
    if (state.prevUrl) {
      fetchInvoices(state.prevUrl);
      setState((prev) => ({
        ...prev,
        pageNumber: Math.max(1, prev.pageNumber - 1),
      }));
    } else {
      showToast("success", "You are on first page");
    }
  }, [state.prevUrl, fetchInvoices]);

  const handlePaginationNext = useCallback(() => {
    if (state.nextUrl) {
      fetchInvoices(state.nextUrl);
      setState((prev) => ({ ...prev, pageNumber: prev.pageNumber + 1 }));
    } else {
      showToast("success", "You are on last page");
    }
  }, [state.nextUrl, fetchInvoices]);

  const handleChangeCurrentPage = useCallback((pageNum) => {
    setState((prev) => ({ ...prev, pageNumber: pageNum }));
  }, []);

  // Checkbox handlers
  const handleCheckboxChange = useCallback((id) => {
    setState((prev) => ({
      ...prev,
      checkedIds: prev.checkedIds.includes(id)
        ? prev.checkedIds.filter((checkedId) => checkedId !== id)
        : [...prev.checkedIds, id],
    }));
  }, []);

  const handleCheckboxChangeAll = useCallback(
    (event) => {
      setState((prev) => ({
        ...prev,
        checkedIds: event.target.checked
          ? state.items.map((item) => item.slug)
          : [],
      }));
    },
    [state.items]
  );

  // Filter handlers
  const handleFilterChange = useCallback((key, value) => {
    if (key === "query") {
      // For query field, update the immediate filter for UI display
      setFilters((prev) => ({ ...prev, [key]: value }));
      // Don't reset page number immediately for query changes
    } else {
      // For other filters, update immediately and reset page
      setFilters((prev) => ({ ...prev, [key]: value }));
      setState((prev) => ({ ...prev, pageNumber: 1 }));
    }
  }, []);

  // Initialize debounced query with initial query value
  useEffect(() => {
    setDebouncedQuery(filters.query);
  }, []);

  // Debounced query effect - updates debounced query after 1 second delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // After 1 second delay, update the debounced query
      setDebouncedQuery(filters.query);
      setState((prev) => ({ ...prev, pageNumber: 1 }));
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [filters.query]);

  const handleSortChange = useCallback((field) => {
    setState((prev) => ({
      ...prev,
      sortBy: prev.sortBy === `-${field}` ? field : `-${field}`,
      sortState: !prev.sortState,
      pageNumber: 1,
    }));
  }, []);

  // Date handlers
  const handleDateChange = useCallback((dateKey, value) => {
    if (!value) {
      setFilters((prev) => ({ ...prev, [dateKey]: "" }));
      return;
    }

    const [year, month, day] = value.split("-");
    const formattedDate = `${month}/${day}/${year}`;
    setFilters((prev) => ({ ...prev, [dateKey]: formattedDate }));
  }, []);

  // Delete handlers
  const deleteInvoice = useCallback(
    async (id) => {
      const willDelete = await swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this data",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!willDelete) {
        swal("Your data is safe!");
        return;
      }

      try {
        await axios.delete(`${BASE_URL}api/v1/sales/inventory/invoice/${id}`);

        // Clear the query filter after successful deletion
        setFilters((prev) => ({ ...prev, query: "" }));
        setDebouncedQuery("");

        await fetchInvoices(
          buildApiUrl({
            limit: ITEMS_PER_PAGE,
            offset,
            query: "",
            invoiceSource: filters.invoiceSource,
            sortBy: state.sortBy,
            date1: filters.date1,
            date2: filters.date2,
            deliveryStatus: filters.deliveryStatus,
            paymentStatus: filters.paymentStatus,
          })
        );

        swal("Your data has been deleted!", { icon: "success" });
      } catch (error) {
        console.error("Error deleting invoice:", error);
        swal("Error deleting invoice", { icon: "error" });
      }
    },
    [fetchInvoices, filters, state.sortBy, offset]
  );

  const deleteMultipleInvoices = useCallback(
    async (ids) => {
      const willDelete = await swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this data",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!willDelete) {
        swal("Your data is safe!");
        return;
      }

      try {
        const deletePromises = ids.map((id) =>
          axios.delete(`${BASE_URL}api/v1/sales/inventory/invoice/${id}`)
        );

        await Promise.all(deletePromises);

        setState((prev) => ({ ...prev, checkedIds: [] }));

        // Clear the query filter after successful deletion
        setFilters((prev) => ({ ...prev, query: "" }));
        setDebouncedQuery("");

        await fetchInvoices(
          buildApiUrl({
            limit: ITEMS_PER_PAGE,
            offset,
            query: "",
            invoiceSource: filters.invoiceSource,
            sortBy: state.sortBy,
            date1: filters.date1,
            date2: filters.date2,
            deliveryStatus: filters.deliveryStatus,
            paymentStatus: filters.paymentStatus,
          })
        );

        swal("Your data has been deleted!", { icon: "success" });
      } catch (error) {
        console.error("Error deleting invoices:", error);
        swal("Error deleting invoices", { icon: "error" });
      }
    },
    [fetchInvoices, filters, state.sortBy, offset]
  );

  // Bulk status change
  const handleBulkStatusChange = useCallback(
    async (ids, field, value) => {
      const willUpdate = await swal({
        title: "Are you sure?",
        text: "Once clicked, you will not be able to go back.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!willUpdate) {
        swal("Your data is safe!");
        return;
      }

      try {
        const updatePromises = ids.map((id) =>
          axios.patch(`${BASE_URL}api/v1/sales/inventory/invoice/${id}/`, {
            [field]: value,
          })
        );

        await Promise.all(updatePromises);

        setState((prev) => ({ ...prev, checkedIds: [] }));
        await fetchInvoices(
          buildApiUrl({
            limit: ITEMS_PER_PAGE,
            offset,
            query: debouncedQuery,
            invoiceSource: filters.invoiceSource,
            sortBy: state.sortBy,
            date1: filters.date1,
            date2: filters.date2,
            deliveryStatus: filters.deliveryStatus,
            paymentStatus: filters.paymentStatus,
          })
        );

        swal("Your data has been updated!", { icon: "success" });
      } catch (error) {
        console.error("Error updating invoices:", error);
        swal("Error updating invoices", { icon: "error" });
      }
    },
    [fetchInvoices, filters, state.sortBy, offset]
  );

  // Print functionality
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Daily Sales Report",
  });

  // Generate data
  const generateData = useCallback(() => {
    if (!filters.date1 || !filters.date2) {
      swal("Please select both start and end dates");
      return;
    }

    if (
      filters.date1 === "undefined/undefined/" ||
      filters.date2 === "undefined/undefined/"
    ) {
      swal("Please select valid dates");
      return;
    }

    fetchInvoices(
      buildApiUrl({
        limit: ITEMS_PER_PAGE,
        offset,
        query: debouncedQuery,
        invoiceSource: filters.invoiceSource,
        sortBy: state.sortBy,
        date1: filters.date1,
        date2: filters.date2,
        deliveryStatus: filters.deliveryStatus,
        paymentStatus: filters.paymentStatus,
      })
    );
  }, [filters, state.sortBy, offset, fetchInvoices]);

  // Navigation handlers
  const handleViewInvoice = useCallback((invoice) => {
    const url =
      invoice.source === 0
        ? `/sales/view-web-invoice/${invoice.slug}`
        : `/sales/view-invoice/${invoice.slug}`;
    window.open(url, "_blank");
  }, []);

  const handleEditInvoice = useCallback((invoice) => {
    if (!has_permissions(can_change_invoice)) {
      swal("Sorry, you are not able to edit invoice.");
      return;
    }

    const url =
      invoice.source === 0
        ? `/sales/edit-web-invoice/${invoice.slug}`
        : `/sales/edit-invoice/${invoice.slug}`;
    window.open(url, "_blank");
  }, []);

  const handleDeleteInvoice = useCallback(
    (invoice) => {
      if (!has_permissions(can_delete_invoice)) {
        swal("Sorry, you are not able to delete invoice.");
        return;
      }

      deleteInvoice(invoice.slug);
    },
    [deleteInvoice]
  );

  // Render helpers
  const renderStatusButton = useCallback((status, choices, className) => {
    const choice = choices[status] || "Unknown";
    return <button className={`status1 ${className}`}>{choice}</button>;
  }, []);

  const renderSortIcon = useCallback(
    (field) => {
      const isDescending = state.sortBy === `-${field}`;
      const Icon = isDescending ? ArrowDown : ArrowUp;

      return (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => handleSortChange(field)}
        >
          <Icon color="white" size={24} weight="bold" />
        </div>
      );
    },
    [state.sortBy, handleSortChange]
  );

  // Early returns
  if (!has_permissions(can_view_invoice)) {
    return <Permission />;
  }

  if (state.isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className="categorey-parent">
        {/* Header Section */}
        <div className="cotagorey-head mb-4">
          <div>
            <h3>Invoice List</h3>
            <input
              style={{ color: "black" }}
              type="text"
              className="filter-input"
              placeholder="Search here"
              value={filters.query}
              onChange={(e) => handleFilterChange("query", e.target.value)}
            />
          </div>

          <div>
            <h6>Prepared from</h6>
            <select
              style={{ color: "black", width: "110px" }}
              className="filter-input"
              value={filters.invoiceSource}
              onChange={(e) =>
                handleFilterChange("invoiceSource", e.target.value)
              }
            >
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <h6>Delivery Status</h6>
            <select
              style={{ color: "black", width: "120px" }}
              className="filter-input"
              value={filters.deliveryStatus}
              onChange={(e) =>
                handleFilterChange("deliveryStatus", e.target.value)
              }
            >
              {DELIVERY_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <h6>Payment Status</h6>
            <select
              style={{ color: "black", width: "100px" }}
              className="filter-input"
              value={filters.paymentStatus}
              onChange={(e) =>
                handleFilterChange("paymentStatus", e.target.value)
              }
            >
              {PAYMENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter_div">
            <div className="my-2 my-lg-0">
              <input
                type="date"
                className="date-input"
                onChange={(e) => handleDateChange("date1", e.target.value)}
              />
              <span className="mx-2 my-auto">To</span>
              <input
                type="date"
                className="date-input"
                onChange={(e) => handleDateChange("date2", e.target.value)}
              />
            </div>

            <div
              className="button-div my-auto mx-2"
              style={{ background: "black", cursor: "pointer" }}
              onClick={generateData}
            >
              <span>Generate</span>
            </div>

            {filteredData.length > 0 && (
              <div
                className="button-div my-auto me-2"
                style={{ background: "black", cursor: "pointer" }}
                onClick={handlePrint}
              >
                <span>Print</span>
              </div>
            )}

            <div
              className="button-div ms-2 my-auto me-2 d-none text-white"
              style={{ background: "black", cursor: "pointer" }}
            >
              <HelperExportComponent
                url={buildApiUrl({
                  limit: ITEMS_PER_PAGE,
                  offset,
                  query: debouncedQuery,
                  invoiceSource: filters.invoiceSource,
                  sortBy: state.sortBy,
                  date1: filters.date1,
                  date2: filters.date2,
                  deliveryStatus: filters.deliveryStatus,
                  export: true,
                })}
                fileName="invoice_export_file.xlsx"
                items_data={state.items}
              />
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {state.checkedIds.length > 0 && (
          <div className="mb-2 d-flex align-items-center gap-2">
            <button
              className="btn text-light"
              style={{
                background: "black",
                cursor: "pointer",
                maxWidth: "120px",
                color: "white",
              }}
              onClick={() => deleteMultipleInvoices(state.checkedIds)}
            >
              Delete
            </button>

            <select
              style={{ color: "black", width: "120px" }}
              className="filter-input"
              onChange={(e) =>
                handleBulkStatusChange(
                  state.checkedIds,
                  "delivery_status",
                  Number(e.target.value)
                )
              }
            >
              <option value="">Select</option>
              {DELIVERY_STATUS_OPTIONS.slice(1).map((option) => (
                <option key={option.value} value={option.value.split("=")[1]}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              style={{ color: "black", width: "100px" }}
              className="filter-input"
              onChange={(e) =>
                handleBulkStatusChange(
                  state.checkedIds,
                  "payment_status",
                  Number(e.target.value)
                )
              }
            >
              <option value="">Select</option>
              {PAYMENT_STATUS_OPTIONS.slice(1).map((option) => (
                <option key={option.value} value={option.value.split("=")[1]}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Main Table */}
        <div style={{ display: "block", overflowX: "auto" }}>
          <table className="table_my">
            <thead
              className="bg-dark text-white head-row"
              style={{ height: "50px", borderTopLeftRadius: "20px !important" }}
            >
              <tr>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "110px",
                  }}
                >
                  <div className="d-flex justify-content-center">
                    <input
                      style={{
                        margin: "0 auto",
                        opacity: "1",
                        width: "17px",
                        height: "17px",
                      }}
                      type="checkbox"
                      checked={
                        state.checkedIds.length > 0 &&
                        state.checkedIds.length === state.items.length
                      }
                      onChange={handleCheckboxChangeAll}
                    />
                  </div>
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "150px",
                  }}
                >
                  <p className="ms-3 my-auto">Invoice No</p>
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "187px",
                  }}
                >
                  Prepared From
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "200px",
                  }}
                >
                  <div
                    style={{ width: "150px" }}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <p className="my-auto">Customer Name</p>
                    {renderSortIcon("bill_to")}
                  </div>
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "110px",
                    padding: "0 4px",
                  }}
                >
                  <div
                    style={{ width: "90px" }}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <p className="my-auto">Total</p>
                    {renderSortIcon("total_amount")}
                  </div>
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "147px",
                  }}
                >
                  Delivery Status
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "150px",
                  }}
                >
                  Delivery Type
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "110px",
                  }}
                >
                  Status
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "160px",
                  }}
                >
                  Pathao Status
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "170px",
                  }}
                >
                  Payment Type
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "150px",
                  }}
                >
                  Create On
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "165px",
                  }}
                >
                  Last Modified
                </th>

                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    borderTopRightRadius: "4px",
                    width: "150px",
                  }}
                >
                  <span className="ms-4">Action</span>
                </th>
              </tr>
            </thead>

            <tbody className="tb">
              {state.items.length > 0 ? (
                state.items.map((invoice) => (
                  <tr className="table-row" key={invoice.slug}>
                    <td>
                      <div className="d-flex justify-content-center">
                        <input
                          type="checkbox"
                          style={{ width: "17px", height: "17px" }}
                          checked={state.checkedIds.includes(invoice.slug)}
                          onChange={() => handleCheckboxChange(invoice.slug)}
                        />
                      </div>
                    </td>

                    <td>
                      <p className="ms-3 my-auto">{invoice.number}</p>
                    </td>

                    <td>{SOURCE_LIST[invoice.source]}</td>

                    <td>{invoice.bill_to}</td>

                    <td>৳ {invoice.total_amount}</td>

                    <td>
                      {invoice.pathao_order_status === "Pickup Cancelled" ? (
                        <button className="status1 bg-danger text-white">
                          Cancelled
                        </button>
                      ) : (
                        renderStatusButton(
                          invoice.delivery_status,
                          DELIVERY_STATUS_CHOICES,
                          (invoice.delivery_status === 0 &&
                            "bg-danger text-white") ||
                            (invoice.delivery_status === 1 &&
                              "c5 text-white") ||
                            (invoice.delivery_status === 2 &&
                              "bg-success text-white")
                        )
                      )}
                    </td>

                    <td>
                      {renderStatusButton(
                        invoice.delivery_type,
                        DELIVERY_TYPE_CHOICES,
                        (invoice.delivery_type === 0 && "c5 text-white") ||
                          (invoice.delivery_type === 1 &&
                            "bg-danger text-white")
                      )}
                    </td>

                    <td>
                      {renderStatusButton(
                        invoice.payment_status,
                        INVOICE_STATUS_CHOICES,
                        (invoice.payment_status === 0 && "i1") ||
                          (invoice.payment_status === 1 && "c4 text-white") ||
                          (invoice.payment_status === 2 && "c3 text-white")
                      )}
                    </td>

                    <td>{invoice.pathao_order_status}</td>

                    <td>
                      {PAYMENT_TYPE_CHOICES[invoice.payment_type]}

                      {invoice?.ssl_validated_details?.tran_id ? (
                        <p>{invoice?.ssl_validated_details?.tran_id}</p>
                      ) : (
                        ""
                      )}
                    </td>

                    <td>{invoice.invoice_date}</td>

                    <td className="ms-1">{invoice.last_modified_by}</td>

                    <td>
                      <div className="d-flex gap-3">
                        <div
                          className="edit-del blue"
                          onClick={() => handleViewInvoice(invoice)}
                        >
                          <ViewIcon />
                        </div>

                        <div
                          className="edit-del green"
                          onClick={() => handleEditInvoice(invoice)}
                        >
                          <EditIcon />
                        </div>

                        <div
                          className="edit-del red"
                          onClick={() => handleDeleteInvoice(invoice)}
                        >
                          <DeleteIcon />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13">
                    <div className="not_found d-flex justify-content-center align-items-center py-4">
                      <h4 className="my-4">No Data Found</h4>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Print Table (Hidden) */}
        <div style={{ display: "none" }} className="pt-4">
          <table
            className="table_my pdf-table my-3 pt-4 print_table"
            ref={printRef}
          >
            <thead
              className="d-none thed w-100 text-center me-4"
              style={{ marginTop: "20px" }}
            >
              <div className="mb-4 pb-4 d-flex justify-content-center align-items-center">
                <img className="pb-3" src={kaarujLogo} width={150} alt="" />
                <p className="mt-2 ms-4">
                  Invoice List -
                  <span className="ms-3">Print Date : {currentDate}</span>
                  <span className="ms-3">
                    Total Item : {filteredData.length}
                  </span>
                </p>
              </div>
            </thead>

            <thead
              className="bg-dark text-white head-row"
              style={{ height: "50px", borderTopLeftRadius: "20px !important" }}
            >
              <tr>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "166px",
                  }}
                >
                  <p className="ms-3 my-auto">Invoice No</p>
                </th>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "187px",
                  }}
                >
                  Prepared By
                </th>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "147px",
                  }}
                >
                  Delivery Status
                </th>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "130px",
                  }}
                >
                  Delivery Type
                </th>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "170px",
                  }}
                >
                  Payment Type
                </th>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "120px",
                  }}
                >
                  Total
                </th>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "130px",
                  }}
                >
                  Create On
                </th>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "110px",
                  }}
                >
                  Status
                </th>
              </tr>
            </thead>

            <tbody className="tb">
              {filteredData.length > 0 ? (
                filteredData.map((invoice) => (
                  <tr className="table-row" key={invoice.slug}>
                    <td>
                      <p className="ms-3 my-auto">{invoice.number}</p>
                    </td>
                    <td>{invoice.prepared_by}</td>
                    <td>
                      {renderStatusButton(
                        invoice.delivery_status,
                        DELIVERY_STATUS_CHOICES,
                        (invoice.delivery_status === 0 &&
                          "bg-danger text-white") ||
                          (invoice.delivery_status === 1 && "c5 text-white") ||
                          (invoice.delivery_status === 2 &&
                            "bg-success text-white")
                      )}
                    </td>
                    <td>
                      {renderStatusButton(
                        invoice.delivery_type,
                        DELIVERY_TYPE_CHOICES,
                        (invoice.delivery_type === 0 && "c5 text-white") ||
                          (invoice.delivery_type === 1 &&
                            "bg-danger text-white")
                      )}
                    </td>
                    <td>{PAYMENT_TYPE_CHOICES[invoice.payment_type]}</td>
                    <td>৳ {invoice.total_amount}</td>
                    <td>{invoice.invoice_date}</td>
                    <td>
                      {renderStatusButton(
                        invoice.payment_status,
                        INVOICE_STATUS_CHOICES,
                        (invoice.payment_status === 0 && "i1") ||
                          (invoice.payment_status === 1 && "c4 text-white") ||
                          (invoice.payment_status === 2 && "c3 text-white")
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">
                    <div className="not_found d-flex justify-content-center align-items-center py-4">
                      <h4 className="my-4">No Data Found</h4>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {state.totalData > 0 && (
          <div className="row pag" style={{ marginTop: "30px" }}>
            <div className="col-6 col-md-5">
              <p
                className="inter"
                style={{ color: "#AEAEB2", fontSize: "16px" }}
              >
                Total Entries:{" "}
                <span style={{ color: "black", fontSize: "14px" }}>
                  {state.totalData}
                </span>
                <span className="ms-2"></span>
                Total Page:{" "}
                <span style={{ color: "black", fontSize: "14px" }}>
                  {Math.ceil(state.totalData / ITEMS_PER_PAGE)}
                </span>
              </p>
            </div>

            <div className="col-12 col-md-6 d-flex paggination-button">
              <div className="row lead_pagination bg-transparent">
                <div className="col-12 col-lg-6 d-flex justify-content-lg-end justify-content-md-end col-md-6 col-sm-6 col-xs-12 pagination_container">
                  <nav aria-label="...">
                    <ul className="pagination pagination-sm">
                      <li className="page-item">
                        <Link
                          onClick={handlePaginationPrevious}
                          className={`${
                            state.prevUrl === null ? "disabled" : ""
                          } page-link pagination_texts pagination_btn font_size_for_small py-1 mt-1 me-3 a`}
                          to="#"
                          tabIndex="-1"
                        >
                          <PreviousIcon />
                        </Link>
                      </li>

                      {[...Array(pageCount || 1)].map((_, index) => (
                        <li
                          key={index}
                          className="page-item"
                          style={{
                            display:
                              index + 1 !== state.pageNumber ? "none" : "block",
                          }}
                        >
                          <Link
                            onClick={() => handleChangeCurrentPage(index + 1)}
                            className={`${
                              index + 1 === state.pageNumber
                                ? "paginationActive"
                                : ""
                            } page-link lead_page_link lead_page_link1 font_size_for_small pagination_texts px-2 text-dark`}
                            to="#"
                          >
                            <span className="px-1 py-1">
                              {state.pageNumber}
                            </span>
                          </Link>
                        </li>
                      ))}

                      <li className="page-item">
                        <Link
                          onClick={handlePaginationNext}
                          className={`${
                            state.nextUrl === null ? "disabled" : ""
                          } page-link pagination_btn font_size_for_small pagination_texts py-1 mt-1 ms-3 a`}
                          to="#"
                        >
                          <NextIcon />
                        </Link>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceList;
